import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Zap, ArrowRight, TrendingUp, Shield, Bot, BarChart3, Globe, Cpu,
  Coins, Landmark, Fingerprint, ChevronDown, Play, Target, RefreshCw,
  Wallet, LineChart, Users, Award, Lock, Clock, ChevronRight,
  MessageCircle, FlaskConical, Server, BookOpen, Languages, Trophy
} from 'lucide-react';

/* ── Animated Terminal ────────────────────────────────────── */
const terminalLines = [
  { text: '$ scanning 6 asset classes...', delay: 0, type: 'cmd' },
  { text: '> BTC/USD  $97,842.15  +2.4%', delay: 600, type: 'data' },
  { text: '> AAPL     $234.18     +0.8%', delay: 900, type: 'data' },
  { text: '> EUR/USD  1.0847      -0.1%', delay: 1200, type: 'data' },
  { text: '// signal: AGT-03 momentum detected BTC', delay: 1800, type: 'signal' },
  { text: '> confidence: 87.3% | strategy: macd_bullish', delay: 2200, type: 'info' },
  { text: '$ executing trade...', delay: 2800, type: 'cmd' },
  { text: 'BUY 0.15 BTC @ $97,842 | filled', delay: 3300, type: 'trade' },
  { text: '> P&L: +$247.30 | Sharpe: 2.14', delay: 3800, type: 'success' },
  { text: '$ risk check passed | drawdown: 1.2%', delay: 4300, type: 'cmd' },
  { text: '// cycle #847 complete | next in 58s', delay: 4800, type: 'signal' },
];

function AnimatedTerminal({ t }) {
  const [visibleLines, setVisibleLines] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    const timers = terminalLines.map((line, i) =>
      setTimeout(() => {
        setVisibleLines(prev => [...prev, line]);
        if (containerRef.current) {
          containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
      }, line.delay)
    );

    const resetTimer = setTimeout(() => {
      setVisibleLines([]);
      timers.forEach(clearTimeout);
    }, 7000);

    const loopTimer = setInterval(() => {
      setVisibleLines([]);
      terminalLines.forEach((line, i) => {
        setTimeout(() => {
          setVisibleLines(prev => [...prev, line]);
        }, line.delay);
      });
    }, 8000);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(resetTimer);
      clearInterval(loopTimer);
    };
  }, []);

  const getColor = (type) => {
    switch (type) {
      case 'cmd': return 'text-slate-500';
      case 'data': return 'text-white';
      case 'signal': return 'text-amber-400';
      case 'info': return 'text-slate-400';
      case 'trade': return 'text-emerald-400 font-bold';
      case 'success': return 'text-emerald-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="bg-[#0A0D12] border border-white/10 overflow-hidden shadow-2xl shadow-emerald-500/5" data-testid="animated-terminal">
      <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#0D1017] border-b border-white/5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-amber-500/60" />
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/60" />
        <span className="ml-3 text-[10px] font-mono text-slate-500 tracking-wider">{t('landing.engineBadge')}</span>
      </div>
      <div ref={containerRef} className="p-4 h-[260px] overflow-y-auto font-mono text-[12px] leading-[1.8] custom-scrollbar">
        {visibleLines.map((line, i) => (
          <div key={i} className={`${getColor(line.type)} animate-[fadeIn_0.3s_ease-out]`}>
            {line.text}
          </div>
        ))}
        <span className="inline-block w-2 h-4 bg-emerald-500/80 animate-pulse ml-0.5" />
      </div>
    </div>
  );
}

/* ── Metric Card for How It Works — Animated ────────────── */
function StepMockup({ step, t }) {
  const [selected, setSelected] = useState('$25,000');
  const [toggles, setToggles] = useState({ 'AGT-01': true, 'AGT-03': true, 'AGT-10': false });

  if (step === 1) {
    return (
      <div className="bg-[#0A0D12] border border-white/10 p-4 max-w-[320px] shadow-xl shadow-emerald-500/5">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{t('landing.simDeposit')}</span>
        </div>
        <div className="grid grid-cols-2 gap-2 mb-3">
          {['$10,000', '$25,000', '$50,000', '$100,000'].map(amt => (
            <button key={amt} onClick={() => setSelected(amt)}
              className={`border p-3 text-center cursor-pointer transition-all duration-200 ${amt === selected ? 'border-emerald-500/50 bg-emerald-500/10 scale-[1.02]' : 'border-white/10 hover:border-white/20'}`}>
              <span className="text-white text-sm font-bold font-mono">{amt}</span>
            </button>
          ))}
        </div>
        <button className="w-full py-2 bg-emerald-500 text-[#030508] text-xs font-bold hover:bg-emerald-400 transition-colors">
          {t('landing.depositLabel')} {selected}
        </button>
      </div>
    );
  }
  if (step === 2) {
    return (
      <div className="bg-[#0A0D12] border border-white/10 p-4 max-w-[320px] shadow-xl shadow-emerald-500/5">
        <div className="flex items-center gap-2 mb-3">
          <Bot className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{t('landing.assignToAgents')}</span>
        </div>
        <div className="space-y-2">
          {[
            { id: 'AGT-01', name: 'MomentumHunter', pnl: '+4.2%' },
            { id: 'AGT-03', name: 'CryptoScalper', pnl: '+7.8%' },
            { id: 'AGT-10', name: 'StatArbEngine', pnl: '+3.1%' },
          ].map(a => (
            <div key={a.id} className="flex items-center justify-between py-2 px-2 bg-[#11141D] border border-white/5">
              <div className="flex items-center gap-2">
                <button onClick={() => setToggles(prev => ({ ...prev, [a.id]: !prev[a.id] }))}
                  className={`w-7 h-4 rounded-full relative transition-colors ${toggles[a.id] ? 'bg-emerald-500' : 'bg-slate-700'}`}>
                  <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${toggles[a.id] ? 'left-3.5' : 'left-0.5'}`} />
                </button>
                <span className="text-xs font-mono text-slate-400">{a.id}</span>
                <span className="text-xs text-white">{a.name}</span>
              </div>
              <span className="text-xs font-mono text-emerald-400">{a.pnl}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="bg-[#0A0D12] border border-white/10 p-4 max-w-[320px] shadow-xl shadow-emerald-500/5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{t('landing.portfolioPnl')}</span>
        </div>
        <span className="text-xs font-mono text-emerald-400">{t('landing.roi')}</span>
      </div>
      <div className="text-2xl font-black text-white font-mono mb-2">$281,300.00</div>
      <div className="h-[60px] flex items-end gap-[3px]">
        {[30, 35, 32, 40, 38, 45, 50, 48, 55, 52, 58, 62, 60, 65, 70, 68, 75, 72, 78, 80].map((h, i) => (
          <div key={i} className="flex-1 bg-emerald-500/40 hover:bg-emerald-500/60 transition-colors"
            style={{ height: `${h}%`, animation: `grow 0.5s ease-out ${i * 0.05}s both` }} />
        ))}
      </div>
      <div className="flex justify-between mt-3 pt-3 border-t border-white/5">
        <div><span className="text-[10px] text-slate-500">{t('common.trades')}</span><p className="text-xs font-mono text-white font-bold">847</p></div>
        <div><span className="text-[10px] text-slate-500">{t('common.winRate')}</span><p className="text-xs font-mono text-emerald-400 font-bold">67.3%</p></div>
        <div><span className="text-[10px] text-slate-500">{t('common.sharpe')}</span><p className="text-xs font-mono text-white font-bold">2.14</p></div>
      </div>
    </div>
  );
}

/* ── Counter Animation ────────────────────────────────────── */
function AnimatedCounter({ end, suffix = '', prefix = '' }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          let start = 0;
          const step = end / 40;
          const timer = setInterval(() => {
            start += step;
            if (start >= end) { setCount(end); clearInterval(timer); }
            else setCount(Math.floor(start));
          }, 30);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
}

/* ── Main Landing Page ────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState(null);

  const goAction = () => navigate(user && user !== false ? '/dashboard' : '/register');

  return (
    <div className="min-h-screen bg-[#030508] text-white overflow-x-hidden" data-testid="landing-page">

      {/* ═══ Nav ═══ */}
      <nav className="sticky top-0 z-50 bg-[#030508]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-white flex items-center justify-center">
              <Zap className="w-5 h-5 text-[#030508]" />
            </div>
            <span className="text-lg font-black tracking-tight" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
              BlackOre<span className="text-emerald-400">X</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#how-it-works" className="text-xs font-mono text-slate-500 hover:text-white transition-colors tracking-wider">{t('landing.howItWorks')}</a>
            <a href="#features" className="text-xs font-mono text-slate-500 hover:text-white transition-colors tracking-wider">{t('landing.features')}</a>
            <a href="#faq" className="text-xs font-mono text-slate-500 hover:text-white transition-colors tracking-wider">{t('landing.sec08Title')}</a>
            <Link to="/leaderboard" className="text-xs font-mono text-slate-500 hover:text-white transition-colors tracking-wider" data-testid="landing-leaderboard-link">{t('landing.leaderboard')}</Link>
          </div>
          <div className="flex items-center gap-3">
            {user && user !== false ? (
              <button onClick={() => navigate('/dashboard')}
                className="px-5 py-2 bg-emerald-500 text-black font-bold text-xs hover:bg-emerald-400 transition-colors"
                data-testid="landing-dashboard-btn">
                DASHBOARD <ArrowRight className="w-3 h-3 inline ml-1" />
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')}
                  className="px-4 py-2 text-slate-400 hover:text-white text-xs font-mono transition-colors"
                  data-testid="landing-login-btn">
                  LOGIN
                </button>
                <button onClick={() => navigate('/register')}
                  className="px-5 py-2 bg-emerald-500 text-black font-bold text-xs hover:bg-emerald-400 transition-colors"
                  data-testid="landing-register-btn">
                  GET STARTED
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ═══ HERO — Split Layout ═══ */}
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left — Copy */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-4 py-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-emerald-400 text-[11px] font-mono tracking-wider">{t('landing.engineRunning')}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
              {t('landing.heroTitleLine1')}<br />
              <span className="text-emerald-400" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>{t('landing.autopilot')}</span>
            </h1>

            <p className="text-base text-slate-400 leading-relaxed max-w-lg">
              {t('landing.heroDesc').split('<1>')[0]}
              <span className="text-white font-medium"> {t('landing.heroDesc').match(/<1>(.*?)<\/1>/)?.[1] || ''} </span>
              {t('landing.heroDesc').split('</1>')[1]}
            </p>

            <div className="flex flex-wrap gap-8">
              {[
                { value: '17', label: t('landing.statAiAgents') },
                { value: '50+', label: t('landing.statInstruments') },
                { value: '24/7', label: t('landing.statAutopilot') },
                { value: '6', label: t('landing.statAssetClasses') },
                { value: '18', label: t('landing.statFeatures') },
              ].map(s => (
                <div key={s.label}>
                  <div className="text-2xl font-black text-emerald-400 font-mono">{s.value}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-[0.15em] mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={goAction}
                className="px-8 py-3.5 bg-emerald-500 text-black font-bold text-sm hover:bg-emerald-400 transition-all hover:-translate-y-[1px] flex items-center gap-2"
                data-testid="hero-cta-btn">
                {t('landing.startTradingNow')} <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => navigate('/leaderboard')}
                className="px-8 py-3.5 border border-white/15 text-white text-sm font-mono hover:bg-white/5 transition-colors"
                data-testid="hero-leaderboard-btn">
                {t('landing.viewLiveLeaderboard')}
              </button>
            </div>
          </div>

          {/* Right — Animated Terminal */}
          <div className="relative">
            <div className="absolute -inset-4 bg-emerald-500/5 blur-3xl" />
            <div className="relative">
              <AnimatedTerminal t={t} />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ /// 01 THE PROBLEM ═══ */}
      <section className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.contextSection')}</span>
              <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-6" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                {t('landing.marketsRunTitle')}<br />
                <span className="text-slate-500">{t('landing.youCant')}</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                {t('landing.sec01Text')}
              </p>
              <p className="text-slate-400 leading-relaxed">
                {t('landing.youNeedSomething')} <span className="text-white font-medium">{t('landing.thinks')}</span>, <span className="text-white font-medium">{t('landing.adapts')}</span>{t('landing.andText')} <span className="text-white font-medium">{t('landing.executes')}</span> {t('landing.forYouRealtime')}
              </p>
            </div>
            <div className="bg-[#090B0F] border border-white/10 p-6 space-y-4">
              <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mb-4">{t('landing.missedOps')}</div>
              {[
                { market: t('landing.catCrypto'), missed: '~340', volatility: t('landing.highVol'), color: 'text-emerald-400 bg-emerald-400/10' },
                { market: t('landing.marketUsEquities'), missed: '~120', volatility: t('landing.medVol'), color: 'text-blue-400 bg-blue-400/10' },
                { market: t('landing.catForex'), missed: '~85', volatility: t('landing.medVol'), color: 'text-amber-400 bg-amber-400/10' },
                { market: t('landing.marketIpAssets'), missed: '~45', volatility: t('landing.lowVol'), color: 'text-purple-400 bg-purple-400/10' },
              ].map(m => (
                <div key={m.market} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <span className="text-sm text-white font-medium">{m.market}</span>
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-mono text-slate-400">{m.missed} {t('landing.signalsLabel')}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 ${m.color}`}>{m.volatility}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ /// 02 THE SOLUTION ═══ */}
      <section className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.solutionSection')}</span>
              <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-6" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
                {t('landing.agentsIntroTitle')}<br />
                <span className="text-emerald-400">{t('landing.neverSleep')}</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-6">
                {t('landing.agentsIntroDesc')}
                {' '}{t('landing.sec03Text').replace(/<\/?[0-9]>/g, '')}
              </p>
              <div className="space-y-3">
                {[
                  { icon: Target, text: t('landing.bulletStrategy') },
                  { icon: Shield, text: t('landing.bulletRisk') },
                  { icon: LineChart, text: t('landing.bulletExplain') },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <item.icon className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-slate-300">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { type: 'MASTER', agents: 2, desc: 'Orquestacion global', color: 'border-purple-500/30 bg-purple-500/5' },
                { type: 'ALPHA', agents: 4, desc: 'Momentum & Trend', color: 'border-emerald-500/30 bg-emerald-500/5' },
                { type: 'ARBITRAGE', agents: 3, desc: 'Cross-market arb', color: 'border-blue-500/30 bg-blue-500/5' },
                { type: 'QUANT', agents: 3, desc: 'Statistical models', color: 'border-cyan-500/30 bg-cyan-500/5' },
                { type: 'EXECUTION', agents: 3, desc: 'Smart order routing', color: 'border-amber-500/30 bg-amber-500/5' },
                { type: 'INTANGIBLE', agents: 2, desc: 'IP Asset trading', color: 'border-purple-500/30 bg-purple-500/5' },
              ].map(item => (
                <div key={item.type} className={`border p-4 ${item.color} transition-all hover:scale-[1.02]`}>
                  <div className="text-xs font-mono font-bold text-white mb-1">{item.type}</div>
                  <div className="text-[10px] text-slate-400 mb-2">{item.desc}</div>
                  <div className="text-lg font-black text-white">{item.agents}</div>
                  <div className="text-[10px] text-slate-500">{t('landing.agents')}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ /// 03 HOW IT WORKS ═══ */}
      <section id="how-it-works" className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.howSection')}</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-16" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.threeSteps')}
          </h2>

          <div className="space-y-20">
            {[
              { num: '01', title: t('landing.step1Title'), desc: t('landing.step1Desc') },
              { num: '02', title: t('landing.step2Title'), desc: t('landing.step2Desc') },
              { num: '03', title: t('landing.step3Title'), desc: t('landing.step3Desc') },
            ].map((step, i) => (
              <div key={step.num} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-4xl font-black text-emerald-500/20 font-mono">{step.num}</span>
                    <div className="h-px flex-1 bg-white/10" />
                  </div>
                  <h3 className="text-xl font-black text-white mb-3 uppercase tracking-wide">{step.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{step.desc}</p>
                </div>
                <div className={`flex justify-center ${i % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="relative">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAyMCAwIEwgMCAwIDAgMjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
                    <StepMockup step={i + 1} t={t} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ /// 04 FEATURES ═══ */}
      <section id="features" className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.capSection')}</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.builtForTraders')}
          </h2>
          <p className="text-slate-400 mb-12 max-w-lg">{t('landing.poweredByAi')}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Bot, title: t('landing.featAutopilot'), desc: t('landing.featAutopilotDesc'), accent: 'emerald' },
              { icon: MessageCircle, title: t('landing.featAiChat'), desc: t('landing.featAiChatDesc'), accent: 'cyan', isNew: true },
              { icon: FlaskConical, title: t('landing.featQuantLab'), desc: t('landing.featQuantLabDesc'), accent: 'purple', isNew: true },
              { icon: Server, title: t('landing.featCompute'), desc: t('landing.featComputeDesc'), accent: 'amber', isNew: true },
              { icon: Shield, title: t('landing.featRisk'), desc: t('landing.featRiskDesc'), accent: 'emerald' },
              { icon: Shield, title: t('landing.featRiskDash'), desc: t('landing.featRiskDashDesc'), accent: 'red' },
              { icon: BarChart3, title: t('landing.featPnl'), desc: t('landing.featPnlDesc'), accent: 'emerald' },
              { icon: TrendingUp, title: t('landing.featAutoInvest'), desc: t('landing.featAutoInvestDesc'), accent: 'emerald' },
              { icon: Bot, title: t('landing.featRoboAdvisor'), desc: t('landing.featRoboAdvisorDesc'), accent: 'cyan', isNew: true },
              { icon: Globe, title: t('landing.featApi'), desc: t('landing.featApiDesc'), accent: 'emerald' },
              { icon: Cpu, title: t('landing.featPipeline'), desc: t('landing.featPipelineDesc'), accent: 'emerald' },
              { icon: Fingerprint, title: t('landing.featIntangi'), desc: t('landing.featIntangiDesc'), accent: 'purple' },
              { icon: Trophy, title: t('landing.featGamification'), desc: t('landing.featGamificationDesc'), accent: 'amber' },
              { icon: BookOpen, title: t('landing.featTradeJournal'), desc: t('landing.featTradeJournalDesc'), accent: 'blue' },
              { icon: Languages, title: t('landing.featMultiCurrency'), desc: t('landing.featMultiCurrencyDesc'), accent: 'cyan' },
              { icon: Lock, title: t('landing.featExplain'), desc: t('landing.featExplainDesc'), accent: 'emerald' },
              { icon: Award, title: t('landing.featLeaderboard'), desc: t('landing.featLeaderboardDesc'), accent: 'amber' },
              { icon: Users, title: t('landing.featOnboarding'), desc: t('landing.featOnboardingDesc'), accent: 'blue', isNew: true },
            ].map(f => (
              <div key={f.title} className="bg-[#090B0F] border border-white/5 p-5 hover:border-emerald-500/20 transition-all group relative">
                {f.isNew && (
                  <span className="absolute top-3 right-3 text-[8px] font-bold px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 tracking-wider">NEW</span>
                )}
                <f.icon className={`w-5 h-5 text-${f.accent}-500 mb-3 group-hover:text-${f.accent}-400 transition-colors`} />
                <h3 className="text-sm font-bold text-white mb-1.5">{f.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ /// 05 MARKETS ═══ */}
      <section className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.marketsSection')}</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-12" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.sixAssets')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { category: t('landing.catCrypto'), items: ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE'], icon: Coins, color: 'text-emerald-400 border-emerald-500/20' },
              { category: t('landing.catEquities'), items: ['AAPL', 'NVDA', 'TSLA', 'MSFT', 'AMZN', 'META', 'GOOGL'], icon: Landmark, color: 'text-blue-400 border-blue-500/20' },
              { category: t('landing.catEtfs'), items: ['SPY', 'QQQ', 'GLD', 'S&P 500', 'NASDAQ'], icon: BarChart3, color: 'text-cyan-400 border-cyan-500/20' },
              { category: t('landing.catForex'), items: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'AUD/USD'], icon: Globe, color: 'text-amber-400 border-amber-500/20' },
              { category: t('landing.catIntangiTrade'), items: ['Patents', 'Trademarks', 'Copyrights', 'Trade Secrets'], icon: Fingerprint, color: 'text-purple-400 border-purple-500/20' },
            ].map(cat => (
              <div key={cat.category} className={`bg-[#090B0F] border p-4 ${cat.color}`}>
                <div className="flex items-center gap-2 mb-3">
                  <cat.icon className={`w-4 h-4`} />
                  <span className="text-xs font-bold uppercase tracking-wider">{cat.category}</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {cat.items.map(item => (
                    <span key={item} className="text-[10px] font-mono text-slate-400 bg-white/5 px-2 py-0.5">{item}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ /// 06 PERFORMANCE ═══ */}
      <section className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.perfSection')}</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-12" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.resultsSpeak')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: 2.14, suffix: '', label: t('landing.metricSharpe'), prefix: '' },
              { value: 17, suffix: '%', label: t('landing.metricReturn'), prefix: '+' },
              { value: 847, suffix: '', label: t('landing.metricCycles'), prefix: '' },
              { value: 99, suffix: '%', label: t('landing.metricUptime'), prefix: '' },
            ].map(m => (
              <div key={m.label} className="bg-[#090B0F] border border-white/10 p-6 text-center">
                <div className="text-3xl md:text-4xl font-black text-emerald-400 font-mono">
                  <AnimatedCounter end={m.value} suffix={m.suffix} prefix={m.prefix} />
                </div>
                <div className="text-[10px] text-slate-500 uppercase tracking-[0.15em] mt-2">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ /// 07 BENCHMARK ═══ */}
      <section className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.benchmarkSection')}</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.benchmarkTitle')}
          </h2>
          <p className="text-slate-400 text-sm mb-10 max-w-xl">{t('landing.benchmarkSubtitle')}</p>

          <div className="overflow-x-auto -mx-6 px-6">
            <div className="min-w-[900px]">
              {/* Header row */}
              <div className="grid grid-cols-7 gap-0 mb-1">
                <div className="p-3" />
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-center relative">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-[#030508] text-[9px] font-bold px-2 py-0.5 tracking-wider">YOU ARE HERE</div>
                  <div className="text-sm font-black text-emerald-400 tracking-wide">BlackOreX</div>
                  <div className="text-[9px] text-emerald-400/60 mt-0.5">{t('landing.benchBlackoreDesc')}</div>
                </div>
                {[
                  { name: 'Kimpton.ai', tag: 'YC', desc: t('landing.benchKimptonDesc') },
                  { name: 'Standard Signal', tag: 'YC', desc: t('landing.benchStdSignalDesc') },
                  { name: 'QuantConnect', tag: '', desc: t('landing.benchQuantDesc') },
                  { name: '3Commas', tag: '', desc: t('landing.bench3CommasDesc') },
                  { name: 'Pionex', tag: '', desc: t('landing.benchPionexDesc') },
                ].map(p => (
                  <div key={p.name} className="p-3 bg-[#090B0F] border border-white/5 text-center">
                    <div className="text-xs font-bold text-white flex items-center justify-center gap-1.5">
                      {p.name}
                      {p.tag && <span className="text-[8px] px-1 py-0.5 bg-orange-500/20 text-orange-400 font-bold">{p.tag}</span>}
                    </div>
                    <div className="text-[9px] text-slate-500 mt-0.5 leading-tight">{p.desc}</div>
                  </div>
                ))}
              </div>

              {/* Data rows */}
              {[
                {
                  label: t('landing.aiAgentsAutonomous'),
                  values: [
                    { text: t('landing.benchAgents17'), highlight: true },
                    { text: t('landing.benchResearch'), dim: true },
                    { text: t('landing.benchFullAuto') },
                    { text: t('landing.benchManual'), dim: true },
                    { text: t('landing.benchBots') },
                    { text: t('landing.benchBuiltIn') },
                  ]
                },
                {
                  label: t('landing.assetClasses'),
                  values: [
                    { text: t('landing.multiAsset'), highlight: true },
                    { text: t('landing.equitiesEtfs') },
                    { text: t('landing.benchNa'), dim: true },
                    { text: t('landing.multiAssetBroker') },
                    { text: t('landing.cryptoOnly'), dim: true },
                    { text: t('landing.cryptoOnly'), dim: true },
                  ]
                },
                {
                  label: t('landing.autopilot247'),
                  values: [
                    { text: '✓', check: true },
                    { text: '✗', cross: true },
                    { text: '✓', check: true },
                    { text: '✓', check: true },
                    { text: '✓', check: true },
                    { text: '✓', check: true },
                  ]
                },
                {
                  label: t('landing.backtestEngine'),
                  values: [
                    { text: '✓', check: true },
                    { text: '✗', cross: true },
                    { text: '✗', cross: true },
                    { text: '✓', check: true },
                    { text: '✗', cross: true },
                    { text: '✗', cross: true },
                  ]
                },
                {
                  label: t('landing.riskManagement'),
                  values: [
                    { text: t('landing.benchInstitutional'), highlight: true },
                    { text: t('landing.benchNone'), dim: true },
                    { text: t('landing.benchInstitutional') },
                    { text: t('landing.benchCustom') },
                    { text: t('landing.benchBasic') },
                    { text: t('landing.benchBasic') },
                  ]
                },
                {
                  label: t('landing.explainability'),
                  values: [
                    { text: '✓', check: true },
                    { text: t('landing.partial') },
                    { text: '✓', check: true },
                    { text: '✗', cross: true },
                    { text: '✗', cross: true },
                    { text: '✗', cross: true },
                  ]
                },
                {
                  label: t('landing.apiSdk'),
                  values: [
                    { text: '✓', check: true },
                    { text: '✗', cross: true },
                    { text: '✗', cross: true },
                    { text: '✓', check: true },
                    { text: '✓', check: true },
                    { text: '✗', cross: true },
                  ]
                },
                {
                  label: t('landing.codeRequired'),
                  values: [
                    { text: t('landing.no'), highlight: true },
                    { text: t('landing.no') },
                    { text: t('landing.no') },
                    { text: t('landing.yes'), dim: true },
                    { text: t('landing.no') },
                    { text: t('landing.no') },
                  ]
                },
                {
                  label: t('landing.minInvestment'),
                  values: [
                    { text: t('landing.benchNoLimit'), highlight: true },
                    { text: t('landing.benchNoLimit') },
                    { text: t('landing.benchQualified'), dim: true },
                    { text: t('landing.benchNoLimit') },
                    { text: t('landing.benchNoLimit') },
                    { text: t('landing.benchNoLimit') },
                  ]
                },
                {
                  label: t('landing.pricing'),
                  values: [
                    { text: t('landing.freeStar'), highlight: true },
                    { text: '$200' + t('landing.perMonth'), dim: true },
                    { text: t('landing.benchNa'), dim: true },
                    { text: '$60' + t('landing.perMonth') },
                    { text: '$49' + t('landing.perMonth') },
                    { text: t('landing.free') },
                  ]
                },
              ].map((row, ri) => (
                <div key={ri} className={`grid grid-cols-7 gap-0 ${ri % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <div className="p-2.5 flex items-center">
                    <span className="text-[11px] font-mono text-slate-400 tracking-wide">{row.label}</span>
                  </div>
                  {row.values.map((v, vi) => (
                    <div key={vi} className={`p-2.5 text-center flex items-center justify-center ${vi === 0 ? 'bg-emerald-500/5 border-x border-emerald-500/20' : 'border-r border-white/5'}`}>
                      {v.check ? (
                        <span className={`text-sm ${vi === 0 ? 'text-emerald-400' : 'text-emerald-500/70'}`}>&#10003;</span>
                      ) : v.cross ? (
                        <span className="text-sm text-red-500/50">&#10007;</span>
                      ) : (
                        <span className={`text-[11px] font-mono ${v.highlight ? 'text-emerald-400 font-bold' : v.dim ? 'text-slate-600' : 'text-slate-400'}`}>{v.text}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-slate-600 mt-4 font-mono">{t('landing.benchNote')}</p>
        </div>
      </section>

      {/* ═══ /// 08 SOCIAL PROOF ═══ */}
      <section className="border-t border-white/5 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.teamSection')}</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-12" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.builtByTraders')}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-[#090B0F] border border-white/10 p-6 lg:col-span-2">
              <p className="text-slate-300 leading-relaxed mb-6 text-sm">
                {t('landing.teamDesc')} <span className="text-white font-medium">{t('landing.quantTrading')}</span>,
                <span className="text-white font-medium"> {t('landing.machineLearning')}</span>, {t('landing.andText')}
                <span className="text-white font-medium"> {t('landing.finInfra')}</span>.
                {' '}{t('landing.teamDescCont')}
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#030508] border border-white/5 p-4 text-center">
                  <div className="text-xl font-black text-white">17</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t('landing.aiAgents')}</div>
                </div>
                <div className="bg-[#030508] border border-white/5 p-4 text-center">
                  <div className="text-xl font-black text-white">7</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t('landing.phasePipeline')}</div>
                </div>
                <div className="bg-[#030508] border border-white/5 p-4 text-center">
                  <div className="text-xl font-black text-white">&lt;60s</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">{t('landing.perCycle')}</div>
                </div>
              </div>
            </div>
            <div className="bg-[#090B0F] border border-emerald-500/20 p-6">
              <div className="text-[10px] font-mono text-emerald-400 uppercase tracking-wider mb-4">{t('landing.technologies')}</div>
              <div className="space-y-3">
                {[
                  'Python + FastAPI Backend',
                  'React.js Dashboard',
                  'MongoDB Time Series',
                  'WebSocket Real-time',
                  'CoinGecko + yFinance APIs',
                  'IntangiHub Ecosystem',
                  'Stripe Integration',
                ].map(tech => (
                  <div key={tech} className="flex items-center gap-2 text-xs text-slate-400">
                    <div className="w-1.5 h-1.5 bg-emerald-500/60" />
                    {tech}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ /// 09 FAQ ═══ */}
      <section id="faq" className="border-t border-white/5 py-24">
        <div className="max-w-3xl mx-auto px-6">
          <span className="text-emerald-500 text-xs font-mono tracking-[0.3em] mb-4 block">{t('landing.faqSection')}</span>
          <h2 className="text-3xl sm:text-4xl font-black mb-12" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.faqTitle')}
          </h2>
          <div className="space-y-2">
            {(t('landing.faq', { returnObjects: true }) || []).map((faq, i) => (
              <div key={i} className="border border-white/5 bg-[#090B0F]" data-testid={`faq-item-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.02] transition-colors">
                  <span className="text-sm font-bold text-white">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-sm text-slate-400 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="border-t border-white/5 py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
            {t('landing.ctaReadyTitle')}
          </h2>
          <p className="text-slate-400 mb-8">
            {t('landing.ctaReadySub')}
          </p>
          <button onClick={goAction}
            className="px-10 py-4 bg-emerald-500 text-black font-bold text-sm hover:bg-emerald-400 transition-all hover:-translate-y-[1px] inline-flex items-center gap-2"
            data-testid="final-cta-btn">
            {t('landing.getStartedFree')} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ═══ Footer ═══ */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-white flex items-center justify-center">
              <Zap className="w-4 h-4 text-[#030508]" />
            </div>
            <span className="text-sm font-black" style={{ fontFamily: "'Instrument Serif', Georgia, serif" }}>
              BlackOre<span className="text-emerald-400">X</span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-[11px] text-slate-600 font-mono">
            <Link to="/leaderboard" className="hover:text-slate-400 transition-colors">{t('leaderboard.title')}</Link>
            <Link to="/developers" className="hover:text-slate-400 transition-colors">{t('landing.footerApiDocs')}</Link>
            <Link to="/profiles" className="hover:text-slate-400 transition-colors">{t('landing.footerLinks')}</Link>
            <span>&copy; {new Date().getFullYear()} BlackOreX</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
